import { useEffect, useMemo } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import { FieldSurveyorOnly } from './FieldSurveyorOnly'
import { useAuth } from '../../context/AuthContext'
import { getFacilities, type FacilityOption } from '../../lib/services/facilities'
import { surveyStore } from '../../lib/offline/surveyStore'
import { getSurvey, startSurvey, type SurveySession } from '../../lib/services/survey'
import { createSiteVisit, getAssignments, type Visit } from '../../lib/services/visits'

function surveyFrameworkForFacility(facility: FacilityOption | null): string {
  const framework = facility?.accreditation_framework
  if (framework === 'aaahc') return 'AAAHC'
  if (framework === 'tjc') return 'TJC'
  if (framework === 'quad_a') return 'QUAD_A'
  if (framework === 'achc') return 'ACHC'
  if (framework === 'state') return 'STATE'
  return 'CMS'
}

function visitPriority(visit: Visit): number {
  if (visit.status === 'in_progress') return 0
  if (visit.status === 'scheduled') return 1
  return 2
}

function selectReusableVisit(visits: Visit[]): Visit | null {
  const reusable = visits
    .filter((visit) => visit.status === 'in_progress' || visit.status === 'scheduled')
    .sort((left, right) => {
      const statusDelta = visitPriority(left) - visitPriority(right)
      if (statusDelta !== 0) return statusDelta
      return String(right.visit_date).localeCompare(String(left.visit_date))
    })

  return reusable[0] ?? null
}

export function VisitNewPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { viewerContext } = useAuth()
  const facilityId = searchParams.get('facilityId')

  const facilitiesQuery = useQuery({
    queryKey: ['facilities'],
    queryFn: getFacilities,
  })

  const assignmentsQuery = useQuery({
    queryKey: ['assignments'],
    queryFn: getAssignments,
  })

  const facility = useMemo(() => {
    if (!facilityId) return null
    return (facilitiesQuery.data ?? []).find((entry) => entry.facility_id === facilityId) ?? null
  }, [facilitiesQuery.data, facilityId])

  const reusableVisit = useMemo(() => {
    if (!facilityId) return null
    return selectReusableVisit(
      (assignmentsQuery.data ?? []).filter((visit) => visit.facility_id === facilityId),
    )
  }, [assignmentsQuery.data, facilityId])

  const startMutation = useMutation({
    mutationFn: async (): Promise<SurveySession> => {
      if (!facilityId) throw new Error('Missing facility selection')
      if (!viewerContext?.user_id) throw new Error('Missing viewer context')

      const framework = surveyFrameworkForFacility(facility)

      // Resume in_progress only when an active survey session still exists.
      // Orphaned in_progress visits (visit row without survey) previously
      // navigated straight to walkthrough and rendered "No active survey".
      if (reusableVisit?.status === 'in_progress') {
        const existing = await getSurvey(reusableVisit.id).catch(() => null)
        if (existing && (existing.status === 'active' || existing.status === 'paused')) {
          return existing
        }
        return startSurvey(reusableVisit.id, framework)
      }

      if (reusableVisit?.status === 'scheduled') {
        return startSurvey(reusableVisit.id, framework)
      }

      const createdVisit = await createSiteVisit({
        facility_id: facilityId,
        visit_type: 'operations',
        assigned_surveyor_id: viewerContext.user_id,
      })

      return startSurvey(createdVisit.id, framework)
    },
    onSuccess: async (session) => {
      await surveyStore.saveSession(session).catch(() => {})
      navigate(`/visits/${session.visit_id}/survey/walkthrough`, { replace: true })
    },
  })

  const { isPending, isSuccess, isError, mutate } = startMutation

  useEffect(() => {
    if (!facilityId) {
      navigate('/facilities', { replace: true })
      return
    }

    if (facilitiesQuery.isLoading || assignmentsQuery.isLoading) {
      return
    }

    if (!facility) {
      navigate('/facilities', { replace: true })
      return
    }

    if (!isPending && !isSuccess && !isError) {
      mutate()
    }
  }, [
    assignmentsQuery.isLoading,
    facility,
    facilityId,
    facilitiesQuery.isLoading,
    isError,
    isPending,
    isSuccess,
    mutate,
    navigate,
  ])

  const loadingMessage = reusableVisit
    ? reusableVisit.status === 'in_progress'
      ? 'Resuming assigned survey...'
      : 'Starting assigned survey...'
    : 'Creating survey visit...'

  return (
    <FieldSurveyorOnly>
    <FieldAppShell eyebrow="Field surveyor" title="Start Survey" backTo="/facilities">
      <div className="space-y-4 px-4 py-6">
        {startMutation.isError ? (
          <div className="fs-surface p-4 text-sm text-red-600">
            Failed to start survey. Return to Facilities and try again.
          </div>
        ) : (
          <div className="fs-surface p-5 text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-[var(--brand-primary)]" />
            <p className="mt-3 text-sm font-semibold text-slate-800">
              {loadingMessage}
            </p>
            {facility ? (
              <p className="mt-1 text-xs text-slate-500">{facility.facility_name}</p>
            ) : null}
          </div>
        )}
      </div>
    </FieldAppShell>
    </FieldSurveyorOnly>
  )
}
